import { useEffect } from "react";

/**
 * Simple intersection observer hook that toggles the `reveal-visible` class
 * on a node to drive CSS-based entrance animations.
 */
export function useReveal(ref, { rootMargin = "0px 0px -10% 0px", threshold = 0.2, once = true } = {}) {
    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("reveal-visible");
                        if (once) observer.unobserve(entry.target);
                    } else if (!once) {
                        entry.target.classList.remove("reveal-visible");
                    }
                });
            },
            { rootMargin, threshold }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [ref, rootMargin, threshold, once]);
}

/**
 * Convenience hook for lists: adds reveal-visible on children sequentially.
 */
export function useRevealStagger(ref, options) {
    useEffect(() => {
        const node = ref.current;
        if (!node) return;

        const children = Array.from(node.children);
        if (!children.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const baseDelay = Number(node.dataset.revealDelay || 0);
                    children.forEach((child, index) => {
                        child.style.transitionDelay = `${baseDelay + index * 80}ms`;
                        child.classList.add("reveal-visible");
                    });
                    observer.unobserve(entry.target);
                });
            },
            {
                rootMargin: options?.rootMargin ?? "0px 0px -10% 0px",
                threshold: options?.threshold ?? 0.2,
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [ref, options]);
}
